import { AxiosResponse } from 'axios';
import $api from '@/http/index';
import { INotification } from '@models/INotification';
import { computeDateBoundaries, DateBoundaries, DateBucketKey } from '@utils/groupNotificationsByDate';
import { getLoadOptionsQuery } from '@utils/loadOptionsConverter';

/** Счётчики одной группы дат: всего писем и непрочитанных. */
export interface GroupCount {
    total: number;
    unread: number;
}

/** Точные счётчики по бакетам дат (с бэка), не зависят от пагинации. */
export type NotificationGroupCounts = Record<DateBucketKey, GroupCount>;

export class NotificationsService {
    /** Список страницы уведомлений: при requireTotalCount бэк отдаёт `{ data, totalCount }`, иначе массив. */
    static async fetch(
        loadOptions: any,
        mode: string,
        signal?: AbortSignal,
    ): Promise<AxiosResponse<INotification[] | { data: INotification[]; totalCount: number }>> {
        return $api.get(`/notifications?mode=${mode}${getLoadOptionsQuery(loadOptions, true)}`, { signal });
    }

    static async fetchDropdownPage(
        skip: number,
        take: number,
        mode: 'all' | 'unread' = 'all',
    ): Promise<{ data: INotification[]; totalCount: number }> {
        const params = new URLSearchParams({ skip: String(skip), take: String(take), mode });
        // Для общей ленты передаём границы дат (как в groupCounts) — бэк отдаёт её в порядке
        // бакет→непрочитанные→прочитанные→дата, стабильном при пагинации (непрочитанные не
        // «выбрасываются» наверх группы при подгрузке). Режим unread (тост) в этом не нуждается.
        if (mode === 'all') {
            const b = computeDateBoundaries(new Date());
            params.set('today', b.today.toISOString());
            params.set('yesterday', b.yesterday.toISOString());
            params.set('weekStart', b.weekStart.toISOString());
            params.set('lastWeekStart', b.lastWeekStart.toISOString());
        }
        const res = await $api.get<{ data: INotification[]; totalCount: number }>(
            `/notifications/dropdown?${params.toString()}`,
        );
        return res.data;
    }

    static async fetchById(id: string): Promise<INotification | null> {
        try {
            const res = await $api.get<INotification>(`/notifications/${id}`);
            return res.data;
        } catch {
            return null;
        }
    }

    static async delete(keys: string[]): Promise<AxiosResponse<void>> {
        return $api.delete('/notifications', { data: keys });
    }

    static async markAsRead(keys: string[]): Promise<AxiosResponse<void>> {
        return $api.post('/notifications/markAsRead', keys);
    }

    /** Пометить прочитанными все уведомления пользователя (для «прочитать все» в дропдауне). */
    static async markAllAsRead(): Promise<AxiosResponse<void>> {
        return $api.post('/notifications/markAllAsRead');
    }

    /** Точное количество непрочитанных по бакетам дат — границы (UTC) задаёт клиент. */
    static async fetchDropdownGroupCounts(b: DateBoundaries): Promise<NotificationGroupCounts> {
        const params = new URLSearchParams({
            today: b.today.toISOString(),
            yesterday: b.yesterday.toISOString(),
            weekStart: b.weekStart.toISOString(),
            lastWeekStart: b.lastWeekStart.toISOString(),
        });
        const res = await $api.get<NotificationGroupCounts>(`/notifications/dropdown/groupCounts?${params.toString()}`);
        return res.data;
    }

    static async remove(key: any): Promise<AxiosResponse<void>> {
        return $api.post('/notifications/markAsRead', key);
    }

    static async getBoundDocumentKey(key: string): Promise<AxiosResponse<string>> {
        return $api.get<string>(`/notifications/boundDocumentKey/${key}`);
    }

    /** Фоновый опрос колокольчика: его отказ — не ошибка страницы для поддержки. */
    static async getUnreadCount(): Promise<AxiosResponse<number>> {
        return $api.get<number>('/notifications/unreadCount', { skipSupportErrorLog: true });
    }
}
